/**
 * @module cercaliagl/view/VContextMenu
 */
import $ from 'jquery';
import BaseView from './BaseView';

/**
 * @typedef {Object} MenuItem
 * @property {string} id
 * @property {string} label
 * @property {string} className
 * @property {Array.<MenuSubItem>|undefined} subMenu
 */

/**
 * @typedef {Object} MenuSubItem
 * @property {string} id
 * @property {string} label
 * @property {string} className
 */

/**
 * @enum {string}
 */
export const EventType = {

};

/**
 * @classdesc
 */
class VContextMenu extends BaseView {
  /**
   * @constructor
   */
  constructor() {
    const jqElement = $('<div class="v-contextmenu mdc-elevation--z4"><ul class="mdc-list collection" role="menu"/></div>');
    super(jqElement);

    /**
     * @private
     * @type {Array.<MenuItem>}
     */
    this.items_ = [];
  }

  /**
   * @param {MenuItem} item Item
   * @param {number=} [position] Position
   */
  addOption(item, position) {
    if (this.indexOf_(item.id) === -1) {
      if (position) {
        this.items_.splice(position, 0, item);
      } else {
        this.items_.push(item);
      }
      this.render_();
    } else {
      console.error(`Option with id "${item.id}" already exists`); // eslint-disable-line no-console
    }
  }

  /**
   * @param {string} id Id
   * @return {boolean} `true` If exists.
   */
  exists(id) {
    return this.indexOf_(id) !== -1;
  }

  /**
   * @param {string} id Id
   */
  removeOption(id) {
    const index = this.indexOf_(id);
    if (index !== -1) {
      const item = this.items_[index];
      this.removeEventListener(`on-${id}`);
      if (item.subMenu) {
        item.subMenu.forEach((subMenuItem) => {
          this.removeEventListener(`on-${subMenuItem.id}`);
        });
      }
      this.items_.splice(index, 1);
      this.render_();
    } else {
      console.warn(`Option with id "${id}" doesn't exist`); // eslint-disable-line no-console
    }
  }

  /**
   * Remove all options
   */
  removeAllOptions() {
    this.items_ = [];
    this.render_();
  }

  /**
   * @param {string} id Id
   * @return {number} `true` if found.
   */
  indexOf_(id) {
    for (let i = 0; i < this.items_.length; i += 1) {
      if (this.items_[i].id === id) {
        return i;
      }
    }
    return -1;
  }

  /**
   * @param {string} id Id
   */
  showOption(id) {
    this.jqElement_.find(`li.${id}`).show();
  }

  /**
   * @param {string} id Id
   */
  hideOption(id) {
    this.jqElement_.find(`li.${id}`).hide();
  }

  /**
   * Toggle visibility
   */
  toggle() {
    if (this.jqElement_.is(':visible')) {
      this.hide();
    } else {
      this.show();
    }
  }

  /**
   * @private
   * @param {Event} event Event
   * @param {MenuItem|MenuSubItem} item Item from menu or submenu item.
   */
  handleClickItem_(event, item) {
    const eventName = `on-${item.id}`;
    this.emit(eventName, event.target);
    this.hide();
    this.reset();
  }

  /**
   * Reset
   */
  reset() {
    this.jqElement_.find('.vitem').removeClass('vselected');
    this.jqElement_.find('[data-submenu]').hide();
  }

  /**
   * @private
   */
  render_() {
    const ulElement = this.jqElement_.find('ul');
    ulElement.empty();
    this.items_.forEach((item) => {
      ulElement.append(
        $(`<li href="#!" class="mdc-list-item vitem collection-item ${item.id}${item.className ? ` ${item.className}` : ''}" role="menuitem">${item.label}</li>`)
          .click((e) => {
            if (!item.subMenu) {
              this.handleClickItem_(e, item);
            } else {
              this.toggleSubmenu(item.id);
            }
          }),
      );
      if (item.subMenu) {
        item.subMenu.forEach((subMenuItem) => {
          ulElement.append(
            $(`<li href="#!" data-submenu="${item.id}" class="mdc-list-item vsubmenu collection-item ${subMenuItem.id}${subMenuItem.className ? ` ${subMenuItem.className}` : ''}" role="menuitem">${subMenuItem.label}</li>`)
              .click((e) => this.handleClickItem_(e, subMenuItem)),
          );
        });
      }
    });
  }

  /**
   * @param {string} id Id
   * @param {boolean} [visible] Visible
   */
  toggleSubmenu(id, visible) {
    const subMenuElements = this.jqElement_.find(`[data-submenu="${id}"]`);
    let display;
    if (visible === undefined) {
      display = subMenuElements.is(':visible') ? 'none' : 'flex';
    } else if (visible === true) {
      display = 'flex';
    } else {
      display = 'none';
    }
    subMenuElements.css('display', display);

    const itemClicked = this.jqElement_.find(`.vitem.${id}`);
    if (subMenuElements.is(':visible')) {
      itemClicked.addClass('mdc-list-item--activated');
    } else {
      itemClicked.removeClass('mdc-list-item--activated');
    }
  }
}

export default VContextMenu;
